import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { generateARN, states, districts } from '../../data/mockData';
import MockInboxLight from '../MockInboxLight';
import { getMockInbox } from '../../utils/registrationUtils';
import { Switch } from '../ui/switch';
import BetaFoundersModal from '../BetaFoundersModal';
import { 
  Building, Users, UserCheck, User, MapPin, MapPinPlus, Package, FileText,
  Shield, CheckCircle, CheckCircle2, ArrowLeft, Plus, Trash2, ArrowRight,
  Calendar, Upload, Phone, Mail, Inbox, Menu, X, Info, Home, LogOut, Smartphone,
  HelpCircle
} from 'lucide-react';

const PartBRegistration = ({ 
  trn, 
  partAData, 
  onBack, 
  onNewRegistration, 
  hideHeader = false,
  activeSection: externalActiveSection,
  onActiveSectionChange,
  completedSections: externalCompletedSections,
  onCompletedSectionsChange
}) => {
  const navigate = useNavigate();
  
  // Use external state if provided (when hideHeader is true), otherwise use local state
  const [localActiveSection, setLocalActiveSection] = useState(1);
  const [localCompletedSections, setLocalCompletedSections] = useState([]);
  
  // Determine which state to use
  const activeSection = hideHeader && externalActiveSection !== undefined ? externalActiveSection : localActiveSection;
  const setActiveSection = hideHeader && onActiveSectionChange ? onActiveSectionChange : setLocalActiveSection;
  const completedSections = hideHeader && externalCompletedSections !== undefined ? externalCompletedSections : localCompletedSections;
  const setCompletedSections = hideHeader && onCompletedSectionsChange ? onCompletedSectionsChange : setLocalCompletedSections;
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generatedARN, setGeneratedARN] = useState('');
  const [showMockInbox, setShowMockInbox] = useState(false);
  const [unreadEmails, setUnreadEmails] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [showEVCModal, setShowEVCModal] = useState(false);
  const [evcOtp, setEvcOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExistingRegModal, setShowExistingRegModal] = useState(false);
  const [newExistingReg, setNewExistingReg] = useState({
    type: '',
    registrationNo: '',
    dateOfRegistration: '',
    fileName: ''
  });
  const [showExistingRegTip, setShowExistingRegTip] = useState(false);
  const [showFileUploadOptions, setShowFileUploadOptions] = useState(false);
  const [showLocalityWarning, setShowLocalityWarning] = useState(false);
  const [promoterPhotoName, setPromoterPhotoName] = useState('');
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [showDemoPhotoModal, setShowDemoPhotoModal] = useState(false);
  const [selectedDemoPhoto, setSelectedDemoPhoto] = useState(null);
  
  // Ref for click-outside handling of photo dropdown
  const photoDropdownRef = useRef(null);

  // Demo robot avatar options - stored locally in /public/avatars/
  const demoAvatars = [
    { id: 1, name: 'Cyborg Alpha', url: '/avatars/robot1.png' },
    { id: 2, name: 'Executive Bot', url: '/avatars/robot2.png' },
    { id: 3, name: 'Tech Guardian', url: '/avatars/robot3.png' },
    { id: 4, name: 'Sentinel Pro', url: '/avatars/robot4.png' }
  ];
  
  // Click outside handler for photo dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (photoDropdownRef.current && !photoDropdownRef.current.contains(event.target)) {
        setShowPhotoOptions(false);
      }
    };
    
    if (showPhotoOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPhotoOptions]);
  
  // EVC OTP for final submission
  const EVC_OTP = '789456';

  // Get lead info from localStorage
  const leadEmail = localStorage.getItem('practiq_lead_email') || '';
  const leadName = localStorage.getItem('practiq_lead_name') || 'User';

  // Existing registration types
  const existingRegTypes = [
    'VAT / TIN',
    'Central Excise',
    'Service Tax',
    'CIN (Company Identification Number)',
    'LLPIN (LLP Identification Number)',
    'IEC (Import Export Code)',
    'Professional Tax (EC)',
    'Professional Tax (RC)',
    'Others'
  ];

  // Form data for all sections
  const [businessDetails, setBusinessDetails] = useState({
    tradeName: '',
    additionalTradeName: '',
    constitution: '',
    optionForComposition: 'no',
    casualTaxablePerson: 'no',
    reasonToRegister: '',
    dateOfCommencement: '',
    dateOfLiability: '',
    district: partAData?.district || '',
    existingRegistrations: []
  });

  const [promoterDetails, setPromoterDetails] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    fatherFirstName: '',
    fatherMiddleName: '',
    fatherLastName: '',
    dob: '',
    gender: '',
    mobile: '',
    email: '',
    telephone: '',
    stdCode: '',
    designation: '',
    din: '',
    pan: partAData?.pan || '',
    passport: '',
    aadhaar: '',
    isCitizen: 'yes',
    address: {
      building: '',
      floor: '',
      street: '',
      locality: '',
      city: '',
      district: partAData?.district || 'Muzaffarpur',
      state: partAData?.state || 'Bihar',
      pincode: '',
      country: 'India',
      premises: '',
      landmark: '',
      latitude: '26.1209',
      longitude: '85.3647'
    },
    isAuthorizedSignatory: 'no'
  });

  const [authorizedSignatory, setAuthorizedSignatory] = useState({
    sameAsPromoter: true,
    firstName: '',
    middleName: '',
    lastName: '',
    designation: '',
    mobile: '',
    email: '',
    pan: '',
    aadhaar: ''
  });

  const [principalPlace, setPrincipalPlace] = useState({
    building: '',
    floor: '',
    street: '',
    locality: '',
    city: '',
    district: partAData?.district || '',
    state: partAData?.state || '',
    pincode: '',
    latitude: '',
    longitude: '',
    officeEmail: '',
    officeMobile: '',
    officePhone: '',
    officeFax: '',
    natureOfPossession: '',
    natureOfActivity: [],
    hasAdditionalPlace: 'no',
    // State Jurisdiction
    stateJurisdiction: '',
    // Center Jurisdiction
    commissionerate: '',
    division: '',
    range: ''
  });

  const [additionalPlaces, setAdditionalPlaces] = useState([]);

  const [goodsServices, setGoodsServices] = useState({
    searchTerm: '',
    selectedItems: []
  });

  const [stateSpecific, setStateSpecific] = useState({
    ptecNo: '',
    exciseLicenseNo: '',
    ptrcNo: '',
    exciseLicenseHolder: ''
  });

  const [aadhaarAuth, setAadhaarAuth] = useState({
    optForAuth: 'yes',
    promoterStatus: 'pending',
    signatoryStatus: 'pending'
  });

  const [verification, setVerification] = useState({
    declaration: false,
    place: ''
  });

  // Section configurations
  const sections = [
    { id: 1, name: 'Business Details', icon: Building },
    { id: 2, name: 'Promoter / Partners', icon: Users },
    { id: 3, name: 'Authorized Signatory', icon: UserCheck },
    { id: 4, name: 'Authorized Representative', icon: User },
    { id: 5, name: 'Principal Place of Business', icon: MapPin },
    { id: 6, name: 'Additional Places of Business', icon: MapPinPlus },
    { id: 7, name: 'Goods and Services', icon: Package },
    { id: 8, name: 'State Specific Information', icon: FileText },
    { id: 9, name: 'Aadhaar Authentication', icon: Shield },
    { id: 10, name: 'Verification', icon: CheckCircle }
  ];

  const constitutionTypes = [
    'Proprietorship',
    'Partnership',
    'Hindu Undivided Family',
    'Private Limited Company',
    'Public Limited Company',
    'Limited Liability Partnership',
    'Society / Club / Trust / AOP',
    'Government Department',
    'Others'
  ];

  const reasonsToRegister = [
    'Voluntary Basis',
    'Crossing Threshold Limit',
    'Inter-State Supply',
    'Casual Taxable Person',
    'Non-Resident Taxable Person',
    'Others'
  ];

  const natureOfPossessionOptions = [
    'Owned',
    'Rented',
    'Leased',
    'Consent',
    'Shared',
    'Others'
  ];

  const businessActivities = [
    'Factory / Manufacturing',
    'Wholesale Business',
    'Retail Business',
    'Warehouse / Depot',
    'Bonded Warehouse',
    'Office / Sale Office',
    'EOU / STP / EHTP',
    'Export',
    'Import',
    'Leasing Business',
    'Recipient of Goods or Services',
    'Supplier of Services',
    'Works Contract',
    'Others'
  ];

  const sampleGoodsServices = [
    { code: '998311', description: 'Management consulting and management services including financial' },
    { code: '998312', description: 'Business consulting services including public relations services' },
    { code: '998313', description: 'Information technology (IT) consulting and support services' },
    { code: '998314', description: 'IT design and development services' },
    { code: '998411', description: 'Accounting, bookkeeping and auditing services' },
    { code: '998412', description: 'Financial auditing services' },
    { code: '998511', description: 'Legal services' },
    { code: '441011', description: 'Rough wood' },
    { code: '630110', description: 'Cotton fabrics' },
    { code: '8471', description: 'Automatic data processing machines' }
  ];

  // Update unread count
  useEffect(() => {
    const updateUnreadCount = () => {
      const inbox = getMockInbox();
      setUnreadEmails(inbox.filter(e => !e.isRead).length);
    };
    updateUnreadCount();
    const interval = setInterval(updateUnreadCount, 1000);
    return () => clearInterval(interval);
  }, [showMockInbox]);

  const handleSaveAndContinue = () => {
    // For Promoter/Partners section, show locality warning
    if (activeSection === 2) {
      setShowLocalityWarning(true);
      return;
    }
    proceedToNextSection();
  };

  const proceedToNextSection = () => {
    if (!completedSections.includes(activeSection)) {
      setCompletedSections([...completedSections, activeSection]);
    }
    if (activeSection < 10) {
      setActiveSection(activeSection + 1);
    }
    setShowLocalityWarning(false);
  };

  const handleAddExistingReg = () => {
    if (newExistingReg.type && newExistingReg.registrationNo) {
      setBusinessDetails({
        ...businessDetails,
        existingRegistrations: [...businessDetails.existingRegistrations, { ...newExistingReg, id: Date.now() }]
      });
      setNewExistingReg({ type: '', registrationNo: '', dateOfRegistration: '', fileName: '' });
      setShowExistingRegModal(false);
      setShowFileUploadOptions(false);
    }
  };

  const removeExistingReg = (id) => {
    setBusinessDetails({
      ...businessDetails,
      existingRegistrations: businessDetails.existingRegistrations.filter(r => r.id !== id)
    });
  };

  const handleSubmitWithEVC = () => {
    setShowEVCModal(true);
  };

  const handleEVCVerify = () => {
    if (evcOtp === EVC_OTP) {
      setIsSubmitting(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setShowEVCModal(false);
        const arn = generateARN();
        setGeneratedARN(arn);
        
        // Save company data to localStorage for GSTR-1/3B interlinking
        const companyData = {
          legalName: partAData?.legalName || businessDetails.tradeName || 'Demo Company Private Limited',
          tradeName: businessDetails.tradeName || partAData?.legalName || 'Demo Company',
          gstin: `07${(partAData?.legalName || 'DEMO').substring(0, 5).toUpperCase().padEnd(5, 'A')}${Math.floor(1000 + Math.random() * 9000)}A1ZM`,
          state: businessDetails.state || partAData?.state || 'Delhi',
          district: businessDetails.district || partAData?.district || 'New Delhi',
          filingPreference: 'Monthly',
          registrationDate: new Date().toLocaleDateString('en-GB'),
          isRegistered: true,
          arn: arn
        };
        localStorage.setItem('practiq_gst_company', JSON.stringify(companyData));
        
        setShowSuccessModal(true);
      }, 1500);
    }
  };

  const handleSubmit = () => {
    const arn = generateARN();
    setGeneratedARN(arn);
    
    // Save company data to localStorage for GSTR-1/3B interlinking
    const companyData = {
      legalName: partAData?.legalName || businessDetails.tradeName || 'Demo Company Private Limited',
      tradeName: businessDetails.tradeName || partAData?.legalName || 'Demo Company',
      gstin: `07${(partAData?.legalName || 'DEMO').substring(0, 5).toUpperCase().padEnd(5, 'A')}${Math.floor(1000 + Math.random() * 9000)}A1ZM`,
      state: businessDetails.state || partAData?.state || 'Delhi',
      district: businessDetails.district || partAData?.district || 'New Delhi',
      filingPreference: 'Monthly',
      registrationDate: new Date().toLocaleDateString('en-GB'),
      isRegistered: true,
      arn: arn
    };
    localStorage.setItem('practiq_gst_company', JSON.stringify(companyData));
    
    setShowSuccessModal(true);
  };

  const addGoodsService = (item) => {
    if (!goodsServices.selectedItems.find(i => i.code === item.code)) {
      setGoodsServices({
        ...goodsServices,
        selectedItems: [...goodsServices.selectedItems, item],
        searchTerm: ''
      });
    }
  };

  const removeGoodsService = (code) => {
    setGoodsServices({
      ...goodsServices,
      selectedItems: goodsServices.selectedItems.filter(i => i.code !== code)
    });
  };

  const filteredGoodsServices = sampleGoodsServices.filter(item =>
    item.code.includes(goodsServices.searchTerm) ||
    item.description.toLowerCase().includes(goodsServices.searchTerm.toLowerCase())
  );

  const renderBusinessDetails = () => (
    <div className="space-y-6">
      {/* Section Header - GST Portal Style */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800">Details of your Business</h2>
        <span className="text-sm text-red-500 flex items-center gap-1">
          <span className="text-red-500">*</span> indicates mandatory fields
        </span>
      </div>

      {/* Row 1: Legal Name, PAN, Date of Creation - 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <Label className="text-sm text-gray-600">Legal Name of the Business</Label>
          <p className="font-semibold text-gray-900 mt-1">{partAData?.legalName || 'Test Business'}</p>
        </div>
        <div>
          <Label className="text-sm text-gray-600">Permanent Account Number (PAN)</Label>
          <div className="bg-gray-200 text-gray-700 px-3 py-2 rounded mt-1 font-mono text-sm">
            {partAData?.pan || 'ABCDE1234F'}
          </div>
        </div>
        <div>
          <Label className="text-sm text-gray-600">Date of Creation of PAN</Label>
          <p className="text-gray-700 mt-1">Pan date not available</p>
        </div>
      </div>

      {/* Row 2: Trade Name + Constitution - 2 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-sm text-gray-600">Trade Name</Label>
          <Input 
            value={businessDetails.tradeName}
            onChange={(e) => setBusinessDetails({...businessDetails, tradeName: e.target.value})}
            placeholder="Enter Trade Name"
            className="mt-1 border-gray-300"
          />
        </div>
        <div>
          <Label className="text-sm text-gray-600">Constitution of Business (Select Appropriate) <span className="text-red-500">*</span></Label>
          <Select value={businessDetails.constitution} onValueChange={(v) => setBusinessDetails({...businessDetails, constitution: v})}>
            <SelectTrigger className="mt-1 border-gray-300">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {constitutionTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 3: Additional Trade Name with inline +ADD and x CANCEL */}
      <div>
        <Label className="text-sm text-gray-600">Additional Trade Name</Label>
        <div className="flex gap-2 mt-1 items-center">
          <Input 
            value={businessDetails.additionalTradeName}
            onChange={(e) => setBusinessDetails({...businessDetails, additionalTradeName: e.target.value})}
            placeholder="Enter Trade Name"
            className="max-w-md border-gray-300"
          />
          <Button 
            size="sm" 
            className="bg-blue-600 hover:bg-blue-700 text-white px-4"
            onClick={() => {
              if (businessDetails.additionalTradeName.trim()) {
                // For now, just clear the field (simulating add)
                setBusinessDetails({...businessDetails, additionalTradeName: ''});
              }
            }}
          >
            <Plus className="w-4 h-4 mr-1" /> ADD
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setBusinessDetails({...businessDetails, additionalTradeName: ''})}
            className="border-gray-300 text-gray-600"
          >
            <X className="w-4 h-4 mr-1" /> CANCEL
          </Button>
        </div>
      </div>

      {/* Row 4: Name of State (disabled) + District dropdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-sm text-gray-600">Name of the State</Label>
          <p className="font-medium text-gray-900 mt-1">{states.find(s => s.code === partAData?.state)?.name || 'Bihar'}</p>
        </div>
        <div>
          <Label className="text-sm text-gray-600">District <span className="text-red-500">*</span></Label>
          <Select 
            value={businessDetails.district || partAData?.district} 
            onValueChange={(v) => setBusinessDetails({...businessDetails, district: v})}
          >
            <SelectTrigger className="mt-1 border-gray-300">
              <SelectValue placeholder="Select District" />
            </SelectTrigger>
            <SelectContent>
              {(districts[partAData?.state] || districts['BH'] || ['District 1', 'District 2']).map(district => (
                <SelectItem key={district} value={district}>{district}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 5: Casual Taxable Person - Toggle Switch */}
      <div className="py-4 border-t border-b">
        <div className="flex items-center gap-3">
          <Label className="text-sm text-gray-700">
            Are you applying for registration as a casual taxable person? 
            <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
          </Label>
          <Switch 
            checked={businessDetails.casualTaxablePerson === 'yes'}
            onCheckedChange={(checked) => setBusinessDetails({...businessDetails, casualTaxablePerson: checked ? 'yes' : 'no'})}
          />
        </div>
      </div>

      {/* Row 6: Option for Composition - Toggle Switch */}
      <div className="py-4 border-b">
        <div className="flex items-center gap-3">
          <Label className="text-sm text-gray-700">
            Option For Composition 
            <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400 cursor-help" />
          </Label>
          <Switch 
            checked={businessDetails.optionForComposition === 'yes'}
            onCheckedChange={(checked) => setBusinessDetails({...businessDetails, optionForComposition: checked ? 'yes' : 'no'})}
          />
        </div>
      </div>

      {/* Row 7: Reason, Date of commencement, Date of liability - 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <Label className="text-sm text-gray-600">Reason to obtain registration <span className="text-red-500">*</span></Label>
          <Select value={businessDetails.reasonToRegister} onValueChange={(v) => setBusinessDetails({...businessDetails, reasonToRegister: v})}>
            <SelectTrigger className="mt-1 border-gray-300">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {reasonsToRegister.map(reason => (
                <SelectItem key={reason} value={reason}>{reason}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm text-gray-600">Date of commencement of Business <span className="text-red-500">*</span></Label>
          <div className="relative mt-1">
            <Input 
              type="date"
              value={businessDetails.dateOfCommencement}
              onChange={(e) => setBusinessDetails({...businessDetails, dateOfCommencement: e.target.value})}
              className="border-gray-300 pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            />
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
        <div>
          <Label className="text-sm text-gray-600">Date on which liability to register arises <span className="text-red-500">*</span></Label>
          <div className="relative mt-1">
            <Input 
              type="date"
              value={businessDetails.dateOfLiability}
              onChange={(e) => setBusinessDetails({...businessDetails, dateOfLiability: e.target.value})}
              className="border-gray-300 pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            />
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Section: Indicate Existing Registrations */}
      <div className="pt-4">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-base font-semibold text-gray-800">Indicate Existing Registrations</h3>
          {/* Info Tip Toggle */}
          <button 
            onClick={() => setShowExistingRegTip(!showExistingRegTip)}
            className="w-5 h-5 bg-blue-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-blue-600 transition-colors"
          >
            ?
          </button>
        </div>

        {/* Educational Tip - Shown when clicked */}
        {showExistingRegTip && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg animate-in slide-in-from-top duration-300">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Info className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-blue-800 mb-2">When are Existing Registrations required?</p>
                  <div className="text-sm text-blue-700 space-y-2">
                    <p className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">✓</span>
                      <span><strong>Proprietorship:</strong> No existing registrations are required. You can skip this section.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">!</span>
                      <span><strong>Company / LLP:</strong> Certificate of Incorporation (CIN/LLPIN) needs to be uploaded.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-orange-600 font-bold">!</span>
                      <span><strong>Partnership:</strong> Partnership Deed registration details may be required.</span>
                    </p>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setShowExistingRegTip(false)}
                className="text-blue-400 hover:text-blue-600 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        {/* Inline form for adding existing registration */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="text-sm text-gray-600">Type of Registration</Label>
            <Select 
              value={newExistingReg.type} 
              onValueChange={(v) => setNewExistingReg({...newExistingReg, type: v === 'none' ? '' : v})}
            >
              <SelectTrigger className="mt-1 border-gray-300">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-- Select --</SelectItem>
                {existingRegTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-gray-600">Registration No. <span className="text-red-500">*</span></Label>
            <Input 
              value={newExistingReg.registrationNo}
              onChange={(e) => setNewExistingReg({...newExistingReg, registrationNo: e.target.value})}
              placeholder=""
              className="mt-1 border-gray-300"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Date of Registration <span className="text-red-500">*</span></Label>
            <div className="relative mt-1">
              <Input 
                type="date"
                value={newExistingReg.dateOfRegistration}
                onChange={(e) => setNewExistingReg({...newExistingReg, dateOfRegistration: e.target.value})}
                className="border-gray-300 pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4"
              onClick={handleAddExistingReg}
            >
              <Plus className="w-4 h-4 mr-1" /> ADD
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setNewExistingReg({ type: '', registrationNo: '', dateOfRegistration: '', fileName: '' })}
              className="border-gray-300 text-gray-600"
            >
              <X className="w-4 h-4 mr-1" /> CANCEL
            </Button>
          </div>
        </div>

        {/* File Upload Section for Existing Registration */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <Label className="text-sm text-gray-600">Upload Registration Document (PDF)</Label>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <Info className="w-3 h-3" /> File with PDF or JPEG format only. Maximum size: 5 MB
          </p>
          
          <div className="mt-3 relative">
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                className="border-gray-400 text-gray-700"
                onClick={() => setShowFileUploadOptions(!showFileUploadOptions)}
              >
                <Upload className="w-4 h-4 mr-1" /> Choose File
              </Button>
              {!newExistingReg.fileName && (
                <span className="text-sm text-gray-500">No file chosen</span>
              )}
            </div>

            {/* File Upload Options Dropdown */}
            {showFileUploadOptions && (
              <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-64 animate-in slide-in-from-top-2 duration-200">
                <div className="p-2">
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer transition-colors">
                    <Upload className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700">Upload from Computer</span>
                    <input 
                      type="file" 
                      accept=".pdf,.jpg,.jpeg"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setNewExistingReg({...newExistingReg, fileName: e.target.files[0].name});
                          setShowFileUploadOptions(false);
                        }
                      }}
                    />
                  </label>
                  <button 
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 rounded text-left transition-colors"
                    onClick={() => {
                      setNewExistingReg({...newExistingReg, fileName: 'Demo_Certificate_of_Incorporation.pdf'});
                      setShowFileUploadOptions(false);
                    }}
                  >
                    <FileText className="w-4 h-4 text-blue-500" />
                    <div>
                      <span className="text-sm text-blue-600 font-medium">Upload Demo PDF</span>
                      <p className="text-xs text-gray-400">Sample Certificate of Incorporation</p>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Show uploaded file */}
          {newExistingReg.fileName && (
            <div className="mt-3 flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">{newExistingReg.fileName}</span>
              <button 
                onClick={() => setNewExistingReg({...newExistingReg, fileName: ''})}
                className="ml-auto text-gray-400 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Display added registrations in a table */}
        {businessDetails.existingRegistrations.length > 0 && (
          <div className="mt-4 border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Type</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Registration No.</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Document</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {businessDetails.existingRegistrations.map((reg) => (
                  <tr key={reg.id} className="border-t">
                    <td className="py-2 px-3">{reg.type}</td>
                    <td className="py-2 px-3">{reg.registrationNo}</td>
                    <td className="py-2 px-3">{reg.dateOfRegistration}</td>
                    <td className="py-2 px-3">
                      {reg.fileName ? (
                        <span className="text-blue-600 text-xs flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {reg.fileName}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">No file</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeExistingReg(reg.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderPromoterPartners = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800">Details of Proprietor</h2>
        <span className="text-sm text-red-500 flex items-center gap-1">
          <span className="text-red-500">*</span> indicates mandatory fields
        </span>
      </div>

      {/* Personal Information Section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-medium mb-4 text-gray-700 flex items-center gap-2">
          <User className="w-4 h-4" /> Personal Information
        </h3>
        
        {/* Name of Person */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Name of Person</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-gray-600">First Name <span className="text-red-500">*</span></Label>
              <Input 
                value={promoterDetails.firstName}
                onChange={(e) => setPromoterDetails({...promoterDetails, firstName: e.target.value})}
                placeholder="First Name"
                className="mt-1 border-gray-300"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Middle Name</Label>
              <Input 
                value={promoterDetails.middleName}
                onChange={(e) => setPromoterDetails({...promoterDetails, middleName: e.target.value})}
                placeholder="Enter Middle Name"
                className="mt-1 border-gray-300"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Last Name</Label>
              <Input 
                value={promoterDetails.lastName}
                onChange={(e) => setPromoterDetails({...promoterDetails, lastName: e.target.value})}
                placeholder="Last Name"
                className="mt-1 border-gray-300"
              />
            </div>
          </div>
        </div>

        {/* Name of Father */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Name of Father</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-gray-600">First Name <span className="text-red-500">*</span></Label>
              <Input 
                value={promoterDetails.fatherFirstName}
                onChange={(e) => setPromoterDetails({...promoterDetails, fatherFirstName: e.target.value})}
                placeholder="Father's First Name"
                className="mt-1 border-gray-300"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Middle Name</Label>
              <Input 
                value={promoterDetails.fatherMiddleName}
                onChange={(e) => setPromoterDetails({...promoterDetails, fatherMiddleName: e.target.value})}
                placeholder="Father's Middle Name"
                className="mt-1 border-gray-300"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-600">Last Name</Label>
              <Input 
                value={promoterDetails.fatherLastName}
                onChange={(e) => setPromoterDetails({...promoterDetails, fatherLastName: e.target.value})}
                placeholder="Father's Last Name"
                className="mt-1 border-gray-300"
              />
            </div>
          </div>
        </div>

        {/* DOB, Mobile, Email row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-sm text-gray-600">Date of Birth <span className="text-red-500">*</span></Label>
            <div className="relative mt-1">
              <Input 
                type="date"
                value={promoterDetails.dob}
                onChange={(e) => setPromoterDetails({...promoterDetails, dob: e.target.value})}
                className="border-gray-300 pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <Label className="text-sm text-gray-600">Mobile Number <span className="text-red-500">*</span></Label>
            <div className="flex gap-1 mt-1">
              <span className="flex items-center px-3 bg-gray-100 border border-gray-300 rounded-l text-sm text-gray-600">+91</span>
              <Input 
                value={promoterDetails.mobile}
                onChange={(e) => setPromoterDetails({...promoterDetails, mobile: e.target.value.replace(/\D/g, '')})}
                placeholder="9876543210"
                maxLength={10}
                className="rounded-l-none border-gray-300"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm text-gray-600">Email Address <span className="text-red-500">*</span></Label>
            <Input 
              type="email"
              value={promoterDetails.email}
              onChange={(e) => setPromoterDetails({...promoterDetails, email: e.target.value})}
              placeholder="email@example.com"
              className="mt-1 border-gray-300"
            />
          </div>
        </div>

        {/* Gender and Telephone row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-gray-600">Gender <span className="text-red-500">*</span></Label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="gender" 
                  value="male" 
                  checked={promoterDetails.gender === 'male'}
                  onChange={(e) => setPromoterDetails({...promoterDetails, gender: e.target.value})}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm flex items-center gap-1"><User className="w-4 h-4 text-blue-500" /> Male</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="gender" 
                  value="female" 
                  checked={promoterDetails.gender === 'female'}
                  onChange={(e) => setPromoterDetails({...promoterDetails, gender: e.target.value})}
                  className="w-4 h-4 text-pink-600"
                />
                <span className="text-sm flex items-center gap-1"><User className="w-4 h-4 text-pink-500" /> Female</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="gender" 
                  value="others" 
                  checked={promoterDetails.gender === 'others'}
                  onChange={(e) => setPromoterDetails({...promoterDetails, gender: e.target.value})}
                  className="w-4 h-4 text-purple-600"
                />
                <span className="text-sm flex items-center gap-1"><Users className="w-4 h-4 text-purple-500" /> Others</span>
              </label>
            </div>
          </div>
          <div>
            <Label className="text-sm text-gray-600">Telephone Number (with STD Code)</Label>
            <div className="flex gap-2 mt-1">
              <Input 
                value={promoterDetails.stdCode}
                onChange={(e) => setPromoterDetails({...promoterDetails, stdCode: e.target.value})}
                placeholder="STD"
                className="w-20 border-gray-300"
              />
              <Input 
                value={promoterDetails.telephone}
                onChange={(e) => setPromoterDetails({...promoterDetails, telephone: e.target.value})}
                placeholder="Enter Telephone Number"
                className="border-gray-300"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Identity Information Section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-medium mb-4 text-gray-700 flex items-center gap-2">
          <Shield className="w-4 h-4" /> Identity Information
        </h3>
        
        {/* Row 1: Designation, DIN, Citizen toggle */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-sm text-gray-600">Designation / Status <span className="text-red-500">*</span></Label>
            <Select value={promoterDetails.designation} onValueChange={(v) => setPromoterDetails({...promoterDetails, designation: v})}>
              <SelectTrigger className="mt-1 border-gray-300">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="proprietor">Proprietor</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="director">Director</SelectItem>
                <SelectItem value="karta">Karta</SelectItem>
                <SelectItem value="managing-director">Managing Director</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-gray-600">Director Identification Number <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.din}
              onChange={(e) => setPromoterDetails({...promoterDetails, din: e.target.value})}
              placeholder="Enter DIN Number"
              className="mt-1 border-gray-300"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Are you a citizen of India?</Label>
            <div className="mt-2">
              <Switch 
                checked={promoterDetails.isCitizen === 'yes'}
                onCheckedChange={(checked) => setPromoterDetails({...promoterDetails, isCitizen: checked ? 'yes' : 'no'})}
              />
            </div>
          </div>
        </div>

        {/* Row 2: PAN, Passport, Aadhaar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm text-gray-600">Permanent Account Number (PAN) <span className="text-red-500">*</span></Label>
            <Input 
              value={promoterDetails.pan}
              onChange={(e) => setPromoterDetails({...promoterDetails, pan: e.target.value.toUpperCase()})}
              placeholder="ABCDE1234F"
              maxLength={10}
              className="mt-1 border-gray-300 uppercase bg-gray-50"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Passport Number (In case of Foreigner)</Label>
            <Input 
              value={promoterDetails.passport}
              onChange={(e) => setPromoterDetails({...promoterDetails, passport: e.target.value})}
              placeholder="Enter Passport Number"
              className="mt-1 border-gray-300"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Aadhaar Number <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.aadhaar}
              onChange={(e) => setPromoterDetails({...promoterDetails, aadhaar: e.target.value.replace(/\D/g, '')})}
              placeholder="Enter Aadhaar Number"
              maxLength={12}
              className="mt-1 border-gray-300"
            />
          </div>
        </div>
      </div>

      {/* Residential Address Section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-medium mb-4 text-gray-700 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Residential Address
        </h3>

        {/* Warning message */}
        <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mb-4 text-xs text-yellow-800">
          <p className="mb-1">a. Please be aware that the GST system incorporates mandatory address validations for accuracy and uniformity. These include front-end validations upon entry and back-end cross-checks with GST system geocoding engine.</p>
          <p>b. Users must ensure that addresses entered align with these validations and any corresponding address proof. Your adherence helps maintain system integrity. Thank you for your cooperation.</p>
        </div>

        {/* Map placeholder with search */}
        <div className="mb-4">
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Enter Address in Latitude, Longitude"
                  className="pl-10 border-gray-300"
                  defaultValue={`${promoterDetails.address.latitude}, ${promoterDetails.address.longitude}`}
                />
              </div>
              <div className="text-xs text-gray-500 bg-white px-2 py-1 border rounded">
                drag the marker to your location
              </div>
            </div>
            {/* Static Map Placeholder */}
            <div className="w-full h-48 bg-gradient-to-br from-green-100 via-green-50 to-blue-100 rounded border overflow-hidden relative">
              {/* Map grid lines */}
              <div className="absolute inset-0 opacity-20">
                <div className="grid grid-cols-8 grid-rows-6 h-full">
                  {[...Array(48)].map((_, i) => (
                    <div key={i} className="border border-gray-300"></div>
                  ))}
                </div>
              </div>
              {/* Roads simulation */}
              <div className="absolute top-1/4 left-0 right-0 h-1 bg-yellow-300 opacity-60"></div>
              <div className="absolute top-1/2 left-1/4 right-1/4 h-0.5 bg-gray-300"></div>
              <div className="absolute left-1/3 top-0 bottom-0 w-0.5 bg-gray-300"></div>
              <div className="absolute left-2/3 top-0 bottom-0 w-1 bg-yellow-300 opacity-60"></div>
              {/* Location names */}
              <div className="absolute top-4 left-4 text-xs text-gray-600 bg-white/80 px-1 rounded">Shiv Mandir</div>
              <div className="absolute top-8 right-8 text-xs text-gray-600 bg-white/80 px-1 rounded">BMS Colony</div>
              <div className="absolute bottom-12 left-8 text-xs text-gray-600 bg-white/80 px-1 rounded">Budha Public School</div>
              <div className="absolute bottom-4 right-4 text-xs text-gray-600 bg-white/80 px-1 rounded">Railway Station</div>
              <div className="absolute top-1/3 left-1/2 text-xs text-gray-600 bg-white/80 px-1 rounded">Jubilee Law College</div>
              {/* Center marker */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full">
                <div className="relative">
                  <MapPin className="w-8 h-8 text-red-500 drop-shadow-lg" />
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full opacity-50"></div>
                </div>
              </div>
              {/* Google Maps style attribution */}
              <div className="absolute bottom-1 right-1 text-[10px] text-gray-500 bg-white/80 px-1">
                Map data ©2024 | Imagery ©2024
              </div>
              {/* Zoom controls */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-1">
                <button className="w-6 h-6 bg-white border rounded shadow text-gray-600 text-sm">+</button>
                <button className="w-6 h-6 bg-white border rounded shadow text-gray-600 text-sm">−</button>
              </div>
            </div>
          </div>
        </div>

        {/* Address Fields Row 1: Country, PIN, State */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-sm text-gray-600">Country <span className="text-red-500">*</span></Label>
            <Select value={promoterDetails.address.country} onValueChange={(v) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, country: v}})}>
              <SelectTrigger className="mt-1 border-gray-300">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="India">India</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-gray-600">PIN Code <span className="text-red-500">*</span> <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.pincode}
              onChange={(e) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, pincode: e.target.value.replace(/\D/g, '')}})}
              placeholder="Enter PIN Code"
              maxLength={6}
              className="mt-1 border-gray-300 bg-gray-50"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">State <span className="text-red-500">*</span> <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.state}
              disabled
              className="mt-1 border-gray-300 bg-gray-100"
            />
          </div>
        </div>

        {/* Address Fields Row 2: District, City, Locality */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-sm text-gray-600">District <span className="text-red-500">*</span> <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.district}
              onChange={(e) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, district: e.target.value}})}
              placeholder="District"
              className="mt-1 border-gray-300"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">City / Town / Village <span className="text-red-500">*</span> <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.city}
              onChange={(e) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, city: e.target.value}})}
              placeholder="City"
              className="mt-1 border-gray-300"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Locality/Sub Locality <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.locality}
              onChange={(e) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, locality: e.target.value}})}
              placeholder="Locality"
              className="mt-1 border-gray-300 bg-gray-50"
            />
          </div>
        </div>

        {/* Address Fields Row 3: Road, Premises, Building */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="text-sm text-gray-600">Road / Street <span className="text-red-500">*</span> <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.street}
              onChange={(e) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, street: e.target.value}})}
              placeholder="Road / Street"
              className="mt-1 border-gray-300 bg-gray-50"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Name of the Premises / Building <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.premises}
              onChange={(e) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, premises: e.target.value}})}
              placeholder="Enter Name of Premises / Building"
              className="mt-1 border-gray-300"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Building No. / Flat No. <span className="text-red-500">*</span> <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.building}
              onChange={(e) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, building: e.target.value}})}
              placeholder="Building No. / Flat No."
              className="mt-1 border-gray-300 bg-gray-50"
            />
          </div>
        </div>

        {/* Address Fields Row 4: Floor, Landmark */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="text-sm text-gray-600">Floor No. <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.floor}
              onChange={(e) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, floor: e.target.value}})}
              placeholder="Enter Floor No."
              className="mt-1 border-gray-300"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Nearby Landmark <HelpCircle className="w-3 h-3 inline ml-1 text-gray-400" /></Label>
            <Input 
              value={promoterDetails.address.landmark}
              onChange={(e) => setPromoterDetails({...promoterDetails, address: {...promoterDetails.address, landmark: e.target.value}})}
              placeholder="Enter Nearby Landmark"
              className="mt-1 border-gray-300"
            />
          </div>
        </div>

        {/* Reset Address Button */}
        <div className="flex justify-center">
          <Button 
            variant="outline" 
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            onClick={() => setPromoterDetails({
              ...promoterDetails, 
              address: {
                ...promoterDetails.address,
                building: '', floor: '', street: '', locality: '', city: '', premises: '', landmark: ''
              }
            })}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> RESET ADDRESS
          </Button>
        </div>
      </div>

      {/* Document Upload Section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-medium mb-4 text-gray-700 flex items-center gap-2">
          <Upload className="w-4 h-4" /> Document Upload
        </h3>
        
        <div>
          <Label className="text-sm text-gray-600">Upload Photograph (of person whose information has been given above) <span className="text-red-500">*</span></Label>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <Info className="w-3 h-3" /> Only JPEG file format is allowed
          </p>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Info className="w-3 h-3" /> Maximum file size for upload is 100 KB
          </p>
          
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-2 relative" ref={photoDropdownRef}>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-gray-400 text-gray-700"
                onClick={() => setShowPhotoOptions(!showPhotoOptions)}
                data-testid="choose-photo-btn"
              >
                Choose File
              </Button>
              
              {/* Dropdown Menu for Photo Options */}
              {showPhotoOptions && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[200px] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <label className="cursor-pointer flex items-center gap-2 px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100">
                    <Upload className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-gray-700">Upload from Computer</span>
                    <input 
                      type="file" 
                      accept=".jpg,.jpeg"
                      className="hidden"
                      data-testid="upload-from-computer-input"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setPromoterPhotoName(e.target.files[0].name);
                          setSelectedDemoPhoto(null);
                          setShowPhotoOptions(false);
                        }
                      }}
                    />
                  </label>
                  <button 
                    className="w-full flex items-center gap-2 px-4 py-3 hover:bg-purple-50 transition-colors text-left"
                    onClick={() => {
                      setShowDemoPhotoModal(true);
                      setShowPhotoOptions(false);
                    }}
                    data-testid="choose-demo-photo-btn"
                  >
                    <Users className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-gray-700">Choose Demo Photo</span>
                  </button>
                </div>
              )}
              
              {/* Display selected file or demo photo */}
              <span className="text-sm text-gray-500">
                {selectedDemoPhoto 
                  ? `Demo: ${selectedDemoPhoto.name}` 
                  : promoterPhotoName || 'No file chosen'}
              </span>
            </div>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">
              <Smartphone className="w-4 h-4 mr-1" /> TAKE PICTURE
            </Button>
            <span className="text-xs text-gray-400">You can use your device camera to take selfie photograph.</span>
          </div>

          {/* Preview of uploaded/selected photo */}
          {(promoterPhotoName || selectedDemoPhoto) && (
            <div className="mt-3 flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded">
              {selectedDemoPhoto && (
                <img 
                  src={selectedDemoPhoto.url} 
                  alt={selectedDemoPhoto.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-green-400"
                />
              )}
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">
                {selectedDemoPhoto ? selectedDemoPhoto.name : promoterPhotoName}
              </span>
              <button 
                onClick={() => {
                  setPromoterPhotoName('');
                  setSelectedDemoPhoto(null);
                }}
                className="ml-auto text-gray-400 hover:text-red-500"
                data-testid="remove-photo-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Demo Photo Selection Modal - Video Game Style Character Selection */}
      {showDemoPhotoModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-xl p-6 max-w-2xl w-full mx-4 shadow-2xl border border-gray-700" data-testid="demo-photo-modal">
            {/* Modal Header */}
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">Select Your Avatar</h3>
              <p className="text-gray-400 text-sm">Choose a demo character for the simulator</p>
            </div>
            
            {/* Character Grid - Video Game Style */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {demoAvatars.map((avatar) => (
                <button
                  key={avatar.id}
                  onClick={() => setSelectedDemoPhoto(avatar)}
                  className={`relative group p-2 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                    selectedDemoPhoto?.id === avatar.id 
                      ? 'bg-gradient-to-br from-blue-600 to-purple-600 ring-2 ring-blue-400 shadow-lg shadow-blue-500/30' 
                      : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                  data-testid={`avatar-option-${avatar.id}`}
                >
                  {/* Avatar Image */}
                  <div className="relative">
                    <img 
                      src={avatar.url} 
                      alt={avatar.name}
                      className="w-full aspect-square rounded-lg object-cover border-2 border-gray-600 group-hover:border-blue-400 transition-colors"
                    />
                    {/* Selection Indicator */}
                    {selectedDemoPhoto?.id === avatar.id && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </div>
                    )}
                    {/* Hover Glow Effect */}
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-t from-blue-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                  {/* Avatar Name */}
                  <p className={`text-center mt-2 text-sm font-medium ${
                    selectedDemoPhoto?.id === avatar.id ? 'text-white' : 'text-gray-300'
                  }`}>
                    {avatar.name}
                  </p>
                </button>
              ))}
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-center gap-4">
              <Button 
                variant="outline"
                onClick={() => {
                  setShowDemoPhotoModal(false);
                  setSelectedDemoPhoto(null);
                }}
                className="px-6 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white"
                data-testid="cancel-demo-photo-btn"
              >
                CANCEL
              </Button>
              <Button 
                onClick={() => {
                  if (selectedDemoPhoto) {
                    setPromoterPhotoName('');
                    setShowDemoPhotoModal(false);
                  }
                }}
                disabled={!selectedDemoPhoto}
                className="px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-demo-photo-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" /> SELECT AVATAR
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Other Information Section */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="font-medium mb-4 text-gray-700">Other Information</h3>
        <div className="flex items-center gap-3">
          <Label className="text-sm text-gray-700">Also Authorized Signatory</Label>
          <Switch 
            checked={promoterDetails.isAuthorizedSignatory === 'yes'}
            onCheckedChange={(checked) => setPromoterDetails({...promoterDetails, isAuthorizedSignatory: checked ? 'yes' : 'no'})}
          />
        </div>
      </div>

      {/* Locality Warning Modal */}
      {showLocalityWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-4xl text-orange-500">!</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Warning</h3>
              <p className="text-gray-600 mb-6">
                Locality/Sub-Locality is not matching with the Pin-Code. Do you want to save the details?
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => proceedToNextSection()}
                  className="px-6"
                >
                  YES
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 px-6"
                  onClick={() => setShowLocalityWarning(false)}
                >
                  NO
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAuthorizedSignatory = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-blue-600" />
          Authorized Signatory
        </h2>
        <span className="text-sm text-red-500">* indicates mandatory fields</span>
      </div>

      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <span className="text-green-800">Promoter/Partner is marked as Authorized Signatory</span>
        </div>
        <p className="text-sm text-green-600 mt-1 ml-7">Details will be auto-filled from Promoter/Partners section</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input value={`${promoterDetails.firstName} ${promoterDetails.lastName}`} disabled className="mt-1 bg-gray-100" />
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={promoterDetails.designation || 'Proprietor'} disabled className="mt-1 bg-gray-100" />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input value={promoterDetails.mobile} disabled className="mt-1 bg-gray-100" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={promoterDetails.email} disabled className="mt-1 bg-gray-100" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderAuthorizedRepresentative = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <User className="w-5 h-5 text-blue-600" />
          Authorized Representative
        </h2>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <User className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600">No Authorized Representative added</p>
            <p className="text-sm text-gray-400 mt-1">This section is optional. Add if you have an authorized representative.</p>
            <Button variant="outline" className="mt-4">
              <Plus className="w-4 h-4 mr-2" /> Add Representative
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderPrincipalPlace = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-600" />
          Principal Place of Business
        </h2>
        <span className="text-sm text-red-500">* indicates mandatory fields</span>
      </div>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium mb-4 text-gray-700">Address Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Building / Premises Name <span className="text-red-500">*</span></Label>
              <Input 
                value={principalPlace.building}
                onChange={(e) => setPrincipalPlace({...principalPlace, building: e.target.value})}
                placeholder="Building Name"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Floor No.</Label>
              <Input 
                value={principalPlace.floor}
                onChange={(e) => setPrincipalPlace({...principalPlace, floor: e.target.value})}
                placeholder="Floor No."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Road / Street <span className="text-red-500">*</span></Label>
              <Input 
                value={principalPlace.street}
                onChange={(e) => setPrincipalPlace({...principalPlace, street: e.target.value})}
                placeholder="Road / Street"
                className="mt-1"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <Label>Locality <span className="text-red-500">*</span></Label>
              <Input 
                value={principalPlace.locality}
                onChange={(e) => setPrincipalPlace({...principalPlace, locality: e.target.value})}
                placeholder="Locality"
                className="mt-1"
              />
            </div>
            <div>
              <Label>City / Town <span className="text-red-500">*</span></Label>
              <Input 
                value={principalPlace.city}
                onChange={(e) => setPrincipalPlace({...principalPlace, city: e.target.value})}
                placeholder="City"
                className="mt-1"
              />
            </div>
            <div>
              <Label>PIN Code <span className="text-red-500">*</span></Label>
              <Input 
                value={principalPlace.pincode}
                onChange={(e) => setPrincipalPlace({...principalPlace, pincode: e.target.value})}
                placeholder="110001"
                maxLength={6}
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium mb-4 text-gray-700">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Office Email</Label>
              <Input 
                type="email"
                value={principalPlace.officeEmail}
                onChange={(e) => setPrincipalPlace({...principalPlace, officeEmail: e.target.value})}
                placeholder="office@business.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Office Mobile</Label>
              <Input 
                value={principalPlace.officeMobile}
                onChange={(e) => setPrincipalPlace({...principalPlace, officeMobile: e.target.value})}
                placeholder="9876543210"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Office Phone (with STD)</Label>
              <Input 
                value={principalPlace.officePhone}
                onChange={(e) => setPrincipalPlace({...principalPlace, officePhone: e.target.value})}
                placeholder="011-12345678"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium mb-4 text-gray-700">Nature of Possession of Premises <span className="text-red-500">*</span></h3>
          <Select value={principalPlace.natureOfPossession} onValueChange={(v) => setPrincipalPlace({...principalPlace, natureOfPossession: v})}>
            <SelectTrigger className="max-w-md">
              <SelectValue placeholder="Select Nature of Possession" />
            </SelectTrigger>
            <SelectContent>
              {natureOfPossessionOptions.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="mt-4 border-2 border-dashed rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">Upload Proof of Principal Place of Business</p>
            <p className="text-xs text-gray-400">PDF or JPEG, max 1 MB</p>
            <Button variant="outline" size="sm" className="mt-2">Choose File</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium mb-4 text-gray-700">Nature of Business Activity <span className="text-red-500">*</span></h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {businessActivities.map(activity => (
              <div key={activity} className="flex items-center gap-2">
                <Checkbox 
                  checked={principalPlace.natureOfActivity.includes(activity)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setPrincipalPlace({...principalPlace, natureOfActivity: [...principalPlace.natureOfActivity, activity]});
                    } else {
                      setPrincipalPlace({...principalPlace, natureOfActivity: principalPlace.natureOfActivity.filter(a => a !== activity)});
                    }
                  }}
                />
                <Label className="text-sm">{activity}</Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* State Jurisdiction */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium mb-4 text-gray-700">State Jurisdiction <span className="text-red-500">*</span></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Sector / Circle / Ward / Charge / Unit</Label>
              <Select value={principalPlace.stateJurisdiction} onValueChange={(v) => setPrincipalPlace({...principalPlace, stateJurisdiction: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select Jurisdiction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ward-1">Ward 1</SelectItem>
                  <SelectItem value="ward-2">Ward 2</SelectItem>
                  <SelectItem value="ward-3">Ward 3</SelectItem>
                  <SelectItem value="circle-1">Circle 1</SelectItem>
                  <SelectItem value="circle-2">Circle 2</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Center Jurisdiction */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium mb-4 text-gray-700">Center Jurisdiction <span className="text-red-500">*</span></h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Commissionerate</Label>
              <Select value={principalPlace.commissionerate} onValueChange={(v) => setPrincipalPlace({...principalPlace, commissionerate: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select Commissionerate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delhi-north">Delhi North</SelectItem>
                  <SelectItem value="delhi-south">Delhi South</SelectItem>
                  <SelectItem value="delhi-east">Delhi East</SelectItem>
                  <SelectItem value="delhi-west">Delhi West</SelectItem>
                  <SelectItem value="mumbai-central">Mumbai Central</SelectItem>
                  <SelectItem value="mumbai-south">Mumbai South</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Division</Label>
              <Select value={principalPlace.division} onValueChange={(v) => setPrincipalPlace({...principalPlace, division: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select Division" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="division-1">Division I</SelectItem>
                  <SelectItem value="division-2">Division II</SelectItem>
                  <SelectItem value="division-3">Division III</SelectItem>
                  <SelectItem value="division-4">Division IV</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Range</Label>
              <Select value={principalPlace.range} onValueChange={(v) => setPrincipalPlace({...principalPlace, range: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="range-1">Range 1</SelectItem>
                  <SelectItem value="range-2">Range 2</SelectItem>
                  <SelectItem value="range-3">Range 3</SelectItem>
                  <SelectItem value="range-4">Range 4</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            <Info className="w-3 h-3 inline mr-1" />
            Center jurisdiction is based on your business location. Contact GST helpdesk for assistance.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
        <Label>Have Additional Place of Business?</Label>
        <RadioGroup value={principalPlace.hasAdditionalPlace} onValueChange={(v) => setPrincipalPlace({...principalPlace, hasAdditionalPlace: v})} className="flex gap-4 ml-4">
          <div className="flex items-center gap-2">
            <RadioGroupItem value="yes" id="additional-yes" />
            <Label htmlFor="additional-yes">Yes</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="no" id="additional-no" />
            <Label htmlFor="additional-no">No</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );

  const renderAdditionalPlaces = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <MapPinPlus className="w-5 h-5 text-blue-600" />
          Additional Places of Business
        </h2>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <MapPinPlus className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600">No additional places of business added</p>
            <p className="text-sm text-gray-400 mt-1">Add if you have branches, warehouses, or other business locations</p>
            <Button variant="outline" className="mt-4">
              <Plus className="w-4 h-4 mr-2" /> Add New Place
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderGoodsServices = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Package className="w-5 h-5 text-blue-600" />
          Goods and Services
        </h2>
        <span className="text-sm text-red-500">* indicates mandatory fields</span>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Label>Search by Name or HSN/SAC Code</Label>
          <div className="flex gap-2 mt-2">
            <Input 
              value={goodsServices.searchTerm}
              onChange={(e) => setGoodsServices({...goodsServices, searchTerm: e.target.value})}
              placeholder="Search goods or services..."
              className="flex-1"
            />
            <Button variant="outline">Search</Button>
          </div>

          {goodsServices.searchTerm && (
            <div className="mt-3 border rounded-lg max-h-48 overflow-y-auto">
              {filteredGoodsServices.map(item => (
                <div 
                  key={item.code}
                  className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                  onClick={() => addGoodsService(item)}
                >
                  <div>
                    <span className="font-mono text-sm text-blue-600">{item.code}</span>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                  <Plus className="w-4 h-4 text-gray-400" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium mb-4 text-gray-700">Selected Goods / Services</h3>
          {goodsServices.selectedItems.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Package className="w-8 h-8 mx-auto mb-2" />
              <p>No goods or services selected</p>
              <p className="text-sm">Search and add items above</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">SI No.</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">HSN/SAC Code</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Description</th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {goodsServices.selectedItems.map((item, index) => (
                    <tr key={item.code} className="border-t">
                      <td className="px-4 py-3 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 text-sm font-mono text-blue-600">{item.code}</td>
                      <td className="px-4 py-3 text-sm">{item.description}</td>
                      <td className="px-4 py-3 text-center">
                        <Button variant="ghost" size="sm" onClick={() => removeGoodsService(item.code)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderStateSpecific = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          State Specific Information
        </h2>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Professional Tax Employee Code (EC) No.</Label>
              <Input 
                value={stateSpecific.ptecNo}
                onChange={(e) => setStateSpecific({...stateSpecific, ptecNo: e.target.value})}
                placeholder="Enter EC No. (if applicable)"
                className="mt-1"
              />
            </div>
            <div>
              <Label>State Excise License No.</Label>
              <Input 
                value={stateSpecific.exciseLicenseNo}
                onChange={(e) => setStateSpecific({...stateSpecific, exciseLicenseNo: e.target.value})}
                placeholder="Enter License No. (if applicable)"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Professional Tax Registration Certificate (RC) No.</Label>
              <Input 
                value={stateSpecific.ptrcNo}
                onChange={(e) => setStateSpecific({...stateSpecific, ptrcNo: e.target.value})}
                placeholder="Enter RC No. (if applicable)"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Name of person in whose name Excise License is held</Label>
              <Input 
                value={stateSpecific.exciseLicenseHolder}
                onChange={(e) => setStateSpecific({...stateSpecific, exciseLicenseHolder: e.target.value})}
                placeholder="Enter Name (if applicable)"
                className="mt-1"
              />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            <Info className="w-4 h-4 inline mr-1" />
            Fill these details only if applicable for your state. Leave blank if not required.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  const renderAadhaarAuth = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          Aadhaar Authentication
        </h2>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-6">
            <Label className="text-base">Do you want to opt for Aadhaar Authentication of Promoter/Partner and Authorized Signatory?</Label>
            <RadioGroup value={aadhaarAuth.optForAuth} onValueChange={(v) => setAadhaarAuth({...aadhaarAuth, optForAuth: v})} className="flex gap-6 mt-3">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="auth-yes" />
                <Label htmlFor="auth-yes">Yes</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="auth-no" />
                <Label htmlFor="auth-no">No</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Select</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Designation</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="px-4 py-3"><Checkbox checked /></td>
                  <td className="px-4 py-3 text-sm">Promoter / Partner</td>
                  <td className="px-4 py-3 text-sm">{promoterDetails.firstName} {promoterDetails.lastName}</td>
                  <td className="px-4 py-3 text-sm">{promoterDetails.designation || 'Proprietor'}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                      Authentication Required
                    </Badge>
                  </td>
                </tr>
                <tr className="border-t">
                  <td className="px-4 py-3"><Checkbox checked /></td>
                  <td className="px-4 py-3 text-sm">Primary Authorized Signatory</td>
                  <td className="px-4 py-3 text-sm">{promoterDetails.firstName} {promoterDetails.lastName}</td>
                  <td className="px-4 py-3 text-sm">{promoterDetails.designation || 'Proprietor'}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                      Authentication Required
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <Info className="w-4 h-4 inline mr-1" />
              OTP will be sent to the mobile number and email linked with Aadhaar for authentication.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderVerification = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-blue-600" />
          Verification
        </h2>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="p-4 border rounded-lg bg-gray-50 mb-6">
            <div className="flex items-start gap-3">
              <Checkbox 
                checked={verification.declaration}
                onCheckedChange={(checked) => setVerification({...verification, declaration: checked})}
              />
              <p className="text-sm text-gray-700 leading-relaxed">
                I hereby solemnly affirm and declare that the information given herein above is true and correct to the best of my knowledge and belief and nothing has been concealed therefrom.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Name of Authorized Signatory</Label>
              <Input value={`${promoterDetails.firstName} ${promoterDetails.lastName}`} disabled className="mt-1 bg-gray-100" />
            </div>
            <div>
              <Label>Designation / Status</Label>
              <Input value={promoterDetails.designation || 'Proprietor'} disabled className="mt-1 bg-gray-100" />
            </div>
            <div>
              <Label>Place <span className="text-red-500">*</span></Label>
              <Input 
                value={verification.place}
                onChange={(e) => setVerification({...verification, place: e.target.value})}
                placeholder="Enter Place"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input value={new Date().toLocaleDateString('en-IN')} disabled className="mt-1 bg-gray-100" />
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <Button 
              onClick={handleSubmitWithEVC}
              disabled={!verification.declaration || !verification.place}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="submit-evc-btn"
            >
              Submit with EVC
            </Button>
            <Button variant="outline" className="flex-1" disabled>
              Submit with DSC
            </Button>
            <Button variant="outline" className="flex-1" disabled>
              Submit with E-Signature
            </Button>
          </div>

          <p className="text-xs text-center text-gray-500 mt-4">
            EVC = Electronic Verification Code | DSC = Digital Signature Certificate
          </p>
        </CardContent>
      </Card>
    </div>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 1: return renderBusinessDetails();
      case 2: return renderPromoterPartners();
      case 3: return renderAuthorizedSignatory();
      case 4: return renderAuthorizedRepresentative();
      case 5: return renderPrincipalPlace();
      case 6: return renderAdditionalPlaces();
      case 7: return renderGoodsServices();
      case 8: return renderStateSpecific();
      case 9: return renderAadhaarAuth();
      case 10: return renderVerification();
      default: return renderBusinessDetails();
    }
  };

  // If hideHeader is true, return only the form content without sidebar/header
  if (hideHeader) {
    return (
      <div className="p-6">
        {/* Section Content */}
        {renderActiveSection()}
        
        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => activeSection > 1 ? setActiveSection(activeSection - 1) : onBack()}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {activeSection === 1 ? 'Back to Dashboard' : 'Previous'}
          </Button>
          
          {activeSection < 10 ? (
            <Button
              onClick={handleSaveAndContinue}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              Save & Continue
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={() => setShowEVCModal(true)}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            >
              Submit with EVC
              <CheckCircle className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* EVC Modal */}
        <Dialog open={showEVCModal} onOpenChange={setShowEVCModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <DialogTitle className="text-center">EVC Verification</DialogTitle>
              <DialogDescription className="text-center">
                Enter the OTP sent to your registered mobile to verify and submit
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex justify-center">
                <div className="bg-gray-100 rounded-xl p-4 text-center">
                  <p className="text-gray-500 text-sm">Your EVC OTP</p>
                  <p className="text-3xl font-mono font-bold text-green-600 tracking-widest mt-2">{EVC_OTP}</p>
                </div>
              </div>
              <div>
                <Label>Enter OTP</Label>
                <Input
                  value={evcOtp}
                  onChange={(e) => setEvcOtp(e.target.value)}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  className="text-center text-xl tracking-widest"
                />
              </div>
              <Button
                onClick={handleEVCVerify}
                disabled={isSubmitting || evcOtp.length !== 6}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {isSubmitting ? 'Submitting...' : 'Verify & Submit Application'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Success Modal */}
        <Dialog open={showSuccessModal} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md">
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <DialogTitle className="text-2xl text-green-700 mb-2">
                Application Submitted!
              </DialogTitle>
              <DialogDescription className="text-gray-600 mb-6">
                Your GST Registration application has been successfully submitted.
              </DialogDescription>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500 mb-1">Application Reference Number (ARN)</p>
                <p className="text-2xl font-mono font-bold text-[#0D1B2A]">{generatedARN}</p>
              </div>
              <Button onClick={() => {
                setShowSuccessModal(false);
                setShowFeedbackModal(true);
              }} className="w-full">
                Continue
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Feedback Modal - shows after GST Registration success */}
        <BetaFoundersModal 
          isOpen={showFeedbackModal} 
          onClose={() => {
            setShowFeedbackModal(false);
            onNewRegistration();
          }} 
        />

        {/* Existing Registration Modal */}
        <Dialog open={showExistingRegModal} onOpenChange={setShowExistingRegModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Existing Registration</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Registration Type *</Label>
                <Select value={newExistingReg.type} onValueChange={(v) => setNewExistingReg({...newExistingReg, type: v})}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {existingRegTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Registration Number *</Label>
                <Input
                  value={newExistingReg.registrationNo}
                  onChange={(e) => setNewExistingReg({...newExistingReg, registrationNo: e.target.value})}
                  placeholder="Enter registration number"
                />
              </div>
              <div>
                <Label>Date of Registration</Label>
                <Input
                  type="date"
                  value={newExistingReg.dateOfRegistration}
                  onChange={(e) => setNewExistingReg({...newExistingReg, dateOfRegistration: e.target.value})}
                />
              </div>
              <Button onClick={handleAddExistingReg} className="w-full">
                Add Registration
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Fixed Sidebar - Completely Fixed, No Scroll */}
      <div className={`fixed inset-y-0 left-0 z-[90] w-64 bg-[#0D1B2A] transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-3 border-b border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-white font-bold text-base">PractiQ</span>
              <button onClick={() => setSidebarOpen(false)} className="text-white hover:bg-white/10 p-1 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-2 p-2 bg-white/10 rounded-lg">
              <p className="text-blue-200 text-[10px]">Part B Registration</p>
              <p className="text-white font-mono text-xs mt-0.5">TRN: {trn}</p>
            </div>
          </div>

          {/* Navigation Sections - Compact, No Scroll */}
          <div className="flex-1 p-2">
            <h3 className="text-gray-400 text-[10px] font-semibold uppercase tracking-wider mb-2 px-2">Sections</h3>
            <div className="space-y-0.5">
              {sections.map((section) => {
                const isCompleted = completedSections.includes(section.id);
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    data-testid={`section-${section.id}`}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-all ${
                      isActive 
                        ? 'bg-blue-500/20 text-blue-300' 
                        : isCompleted 
                          ? 'text-green-400 hover:bg-white/5'
                          : 'text-gray-300 hover:bg-white/5'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-medium flex-shrink-0 ${
                      isActive 
                        ? 'bg-blue-500 text-white' 
                        : isCompleted 
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-600 text-gray-300'
                    }`}>
                      {isCompleted ? <CheckCircle2 className="w-2.5 h-2.5" /> : section.id}
                    </span>
                    <span className="text-[11px] truncate">{section.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Actions - Compact */}
          <div className="p-2 border-t border-white/10">
            <button
              onClick={() => setShowMockInbox(true)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-gray-300 hover:bg-white/10 rounded transition-colors relative"
              data-testid="partb-mock-inbox"
            >
              <Inbox className="w-3.5 h-3.5" />
              <span className="text-[11px]">Mock Inbox</span>
              {unreadEmails > 0 && (
                <span className="absolute right-2 min-w-[14px] h-3.5 px-1 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center">
                  {unreadEmails}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/gst')}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-gray-300 hover:bg-white/10 rounded transition-colors"
            >
              <Home className="w-3.5 h-3.5" />
              <span className="text-[11px]">GST Dashboard</span>
            </button>
          </div>

          {/* User Section - Compact */}
          <div className="p-2 border-t border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-[10px] truncate">{leadName}</p>
                <p className="text-gray-400 text-[9px] truncate">{leadEmail}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        {/* Floating Hamburger Button - Always visible when sidebar/header hidden */}
        {(!sidebarOpen || !headerVisible) && (
          <div className="fixed top-3 left-3 z-[100] flex gap-2">
            {!sidebarOpen && (
              <button 
                onClick={() => setSidebarOpen(true)}
                className="p-2.5 bg-[#0D1B2A] text-white hover:bg-[#1B2838] rounded-lg shadow-lg"
                title="Open Sidebar"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
            {!headerVisible && (
              <button 
                onClick={() => setHeaderVisible(true)}
                className="p-2.5 bg-[#0D1B2A] text-white hover:bg-[#1B2838] rounded-lg shadow-lg flex items-center gap-2"
                title="Show Header"
              >
                <ArrowRight className="w-4 h-4 rotate-90" />
                <span className="text-xs">Show Header</span>
              </button>
            )}
          </div>
        )}

        {/* Collapsible Top Header */}
        <header className={`bg-[#0D1B2A] text-white py-2 px-4 flex items-center justify-between sticky top-0 z-30 transition-all duration-300 ${headerVisible ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0 overflow-hidden py-0'}`}>
          <div className="flex items-center gap-3">
            {!sidebarOpen && (
              <button 
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
            <div>
              <div className="flex items-center gap-2 text-blue-200 text-xs">
                <span className="cursor-pointer hover:text-white" onClick={() => navigate('/')}>Home</span>
                <span>/</span>
                <span className="cursor-pointer hover:text-white" onClick={() => navigate('/gst')}>GST Services</span>
                <span>/</span>
                <span>Registration - Part B</span>
              </div>
              <h1 className="text-base font-semibold">Registration Application - Part B</h1>
              <p className="text-[10px] text-gray-400">TRN: {trn}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMockInbox(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-white hover:bg-white/10 rounded-lg relative"
            >
              <Inbox className="w-4 h-4" />
              <span className="text-xs">Mock Inbox</span>
              {unreadEmails > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-[10px] rounded-full flex items-center justify-center">
                  {unreadEmails}
                </span>
              )}
            </button>
            <Badge variant="secondary" className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">
              Expires in 15 days
            </Badge>
            <Badge variant="outline" className="text-blue-300 border-blue-400/50 text-xs">
              Status: Draft
            </Badge>
            <button 
              onClick={() => setHeaderVisible(false)}
              className="p-1.5 hover:bg-white/10 rounded-lg ml-2"
              title="Hide Header"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Progress Bar */}
        <div className="bg-white border-b px-4 py-2 flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Application Progress</span>
            <span className="text-xs font-medium text-blue-600">{Math.round((completedSections.length / 10) * 100)}% Complete</span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
              style={{ width: `${(completedSections.length / 10) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <Card className="max-w-4xl mx-auto shadow-lg">
            <CardContent className="p-6">
              {renderActiveSection()}

              {activeSection < 10 && (
                <div className="flex justify-between mt-8 pt-6 border-t">
                  {activeSection > 1 && (
                    <Button 
                      variant="outline" 
                      onClick={() => setActiveSection(activeSection - 1)}
                      className="flex items-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button 
                    onClick={handleSaveAndContinue} 
                    className="bg-[#0D1B2A] hover:bg-[#1B2838] flex items-center gap-2"
                    data-testid="save-continue-btn"
                  >
                    SAVE & CONTINUE
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* EVC OTP Modal with Mobile Animation */}
      {showEVCModal && (
        <>
          {/* Blurred Background */}
          <div 
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
            onClick={() => setShowEVCModal(false)}
          />
          
          {/* Central Modal */}
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto animate-in zoom-in-95 fade-in duration-300"
              style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)' }}
              data-testid="evc-modal"
            >
              <div className="relative px-6 pt-6 pb-4 border-b border-gray-100">
                <button
                  onClick={() => setShowEVCModal(false)}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">EVC Verification</h2>
                  <p className="text-sm text-gray-500 mt-1">Enter OTP to submit your application</p>
                </div>
              </div>

              <div className="px-6 py-6 space-y-5">
                <div>
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-green-500" />
                    Mobile OTP
                  </Label>
                  <Input
                    value={evcOtp}
                    onChange={(e) => setEvcOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="mt-2 text-center text-2xl font-mono tracking-[0.5em] h-14 border-2 focus:border-blue-500"
                    data-testid="evc-otp-input"
                  />
                  <p className="text-xs text-gray-400 mt-1.5">Check your registered mobile for OTP</p>
                </div>
              </div>

              <div className="px-6 pb-6">
                <Button
                  onClick={handleEVCVerify}
                  disabled={isSubmitting || evcOtp.length !== 6}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 h-12 text-base font-medium shadow-lg"
                  data-testid="evc-verify-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Submitting Application...
                    </span>
                  ) : (
                    <>
                      VALIDATE & SUBMIT
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile Phone Animation */}
          <div 
            className="fixed bottom-4 right-4 z-[80] animate-in slide-in-from-bottom-full duration-700 ease-out"
            style={{ animationDelay: '200ms' }}
          >
            <div className="relative">
              <div className="w-48 h-[340px] bg-gray-900 rounded-[2.5rem] p-1.5 shadow-2xl">
                <div className="w-full h-full bg-gradient-to-b from-slate-800 to-slate-900 rounded-[2rem] overflow-hidden relative">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-900 rounded-b-xl"></div>
                  
                  <div className="flex justify-between items-center px-5 pt-7 text-white text-[10px]">
                    <span className="font-medium">9:41</span>
                    <div className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
                      </svg>
                      <span>5G</span>
                      <div className="w-5 h-2.5 border border-white rounded-sm ml-1">
                        <div className="w-3.5 h-full bg-green-400 rounded-sm"></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 mx-2.5 animate-in slide-in-from-top duration-500" style={{ animationDelay: '500ms' }}>
                    <div className="bg-white rounded-2xl p-3.5 shadow-xl">
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-md">
                          <Shield className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 text-xs">GST Portal</p>
                          <p className="text-[10px] text-gray-500">now</p>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                        <p className="text-[11px] text-center text-blue-600 font-semibold mb-1">EVC VERIFICATION OTP</p>
                        <p className="text-[11px] text-gray-600 mb-2 text-center">Your OTP for GST Registration:</p>
                        <p className="text-2xl font-mono font-bold text-blue-600 tracking-widest text-center">
                          {EVC_OTP}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-2 text-center">Valid for 10 mins. Do not share.</p>
                      </div>
                    </div>
                  </div>

                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 bg-white/30 rounded-full"></div>
                </div>
              </div>
              <div className="absolute -inset-2 bg-gradient-to-t from-blue-500/20 to-transparent rounded-[3rem] -z-10 blur-xl"></div>
            </div>
          </div>
        </>
      )}

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              Application Submitted Successfully!
            </DialogTitle>
            <DialogDescription className="text-center">
              Your GST Registration Application has been submitted.
            </DialogDescription>
          </DialogHeader>
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-2">Application Reference Number (ARN)</p>
            <p className="text-2xl font-bold text-blue-600 font-mono">{generatedARN}</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg mb-4">
            <p className="text-sm text-blue-800 text-center">
              <Info className="w-4 h-4 inline mr-1" />
              Your application will be processed within 3-7 working days. You will receive updates via email.
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/gst')} className="flex-1 bg-[#0D1B2A] hover:bg-[#1B2838]">
              Back to Dashboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mock Inbox Light */}
      <MockInboxLight isOpen={showMockInbox} onClose={() => setShowMockInbox(false)} />

      {/* Existing Registration Modal */}
      <Dialog open={showExistingRegModal} onOpenChange={setShowExistingRegModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Add Existing Registration
            </DialogTitle>
            <DialogDescription>
              Enter details of your existing registrations like VAT, Service Tax, CIN, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Registration Type <span className="text-red-500">*</span></Label>
              <Select 
                value={newExistingReg.type} 
                onValueChange={(v) => setNewExistingReg({...newExistingReg, type: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select Registration Type" />
                </SelectTrigger>
                <SelectContent>
                  {existingRegTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Registration Number <span className="text-red-500">*</span></Label>
              <Input 
                value={newExistingReg.registrationNo}
                onChange={(e) => setNewExistingReg({...newExistingReg, registrationNo: e.target.value})}
                placeholder="Enter Registration Number"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {newExistingReg.type === 'CIN (Company Identification Number)' && 'Format: U12345MH2020PTC123456'}
                {newExistingReg.type === 'VAT / TIN' && 'Format: 27XXXXX1234V'}
                {newExistingReg.type === 'Service Tax' && 'Format: AAXXXXXXXXST001'}
              </p>
            </div>
            <div>
              <Label>Date of Registration</Label>
              <Input 
                type="date"
                value={newExistingReg.dateOfRegistration}
                onChange={(e) => setNewExistingReg({...newExistingReg, dateOfRegistration: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowExistingRegModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddExistingReg}
              disabled={!newExistingReg.type || !newExistingReg.registrationNo}
              className="bg-[#0D1B2A] hover:bg-[#1B2838]"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Registration
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartBRegistration;
